import type { DragMoveEvent, DragStartEvent } from "@dnd-kit/core";
import { PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { navigationTreeStore, useNavigationTreeStore } from "../store/navigationTreeStore";
import { AUTO_EXPAND_DELAY_MS, DRAG_ACTIVATION_DISTANCE } from "../utils/constants";
import { getNavigationAutoScrollDelta } from "../utils/navigationAutoScroll";
import { invalidateNavigationCollisionDetection } from "../utils/navigationCollisionDetection";

const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE } };

export const useNavigationDndBase = (containerRef: RefObject<HTMLDivElement | null>) => {
	const { setDragging, setDragTarget } = useNavigationTreeStore((s) => ({
		setDragging: s.setDragging,
		setDragTarget: s.setDragTarget,
	}));
	const autoExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const autoExpandTargetRef = useRef<string | null>(null);
	const pointerYRef = useRef<number>(0);
	const hasPointerPositionRef = useRef(false);
	const autoScrollFrameRef = useRef<number | null>(null);

	useEffect(() => {
		const onPointerMove = (e: PointerEvent) => {
			pointerYRef.current = e.clientY;
			hasPointerPositionRef.current = true;
		};
		window.addEventListener("pointermove", onPointerMove);
		return () => window.removeEventListener("pointermove", onPointerMove);
	}, []);

	const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));

	const stopAutoScroll = useCallback(() => {
		if (autoScrollFrameRef.current === null) return;
		cancelAnimationFrame(autoScrollFrameRef.current);
		autoScrollFrameRef.current = null;
	}, []);

	const startAutoScroll = useCallback(() => {
		if (autoScrollFrameRef.current !== null) return;

		const tick = () => {
			const scrollContainer = containerRef.current?.closest<HTMLElement>('[data-sidebar="content"]');
			if (!scrollContainer) {
				autoScrollFrameRef.current = null;
				return;
			}

			const delta = getNavigationAutoScrollDelta(pointerYRef.current, scrollContainer.getBoundingClientRect());
			if (delta !== 0) {
				const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
				const nextScrollTop = Math.max(0, Math.min(maxScrollTop, scrollContainer.scrollTop + delta));
				if (nextScrollTop !== scrollContainer.scrollTop) {
					scrollContainer.scrollTop = nextScrollTop;
					invalidateNavigationCollisionDetection();
				}
			}

			autoScrollFrameRef.current = requestAnimationFrame(tick);
		};

		autoScrollFrameRef.current = requestAnimationFrame(tick);
	}, [containerRef]);

	useEffect(() => stopAutoScroll, [stopAutoScroll]);

	const cancelAutoExpand = useCallback(() => {
		if (autoExpandTimerRef.current !== null) {
			clearTimeout(autoExpandTimerRef.current);
			autoExpandTimerRef.current = null;
		}
		autoExpandTargetRef.current = null;
	}, []);

	const scheduleAutoExpand = useCallback(
		(overId: string) => {
			if (autoExpandTargetRef.current === overId) return;
			cancelAutoExpand();
			autoExpandTargetRef.current = overId;
			autoExpandTimerRef.current = setTimeout(() => {
				navigationTreeStore.getState().toggleExpanded(overId, true);
				autoExpandTimerRef.current = null;
			}, AUTO_EXPAND_DELAY_MS);
		},
		[cancelAutoExpand],
	);

	const onDragStart = useCallback(
		({ active }: DragStartEvent) => {
			setDragging(String(active.id));
		},
		[setDragging],
	);

	const finishDrag = useCallback(() => {
		cancelAutoExpand();
		stopAutoScroll();
		setDragging(null);
		setDragTarget(null);
	}, [cancelAutoExpand, stopAutoScroll, setDragging, setDragTarget]);

	const trackPointer = useCallback(({ activatorEvent, delta }: Pick<DragMoveEvent, "activatorEvent" | "delta">) => {
		const pointerY = hasPointerPositionRef.current
			? pointerYRef.current
			: activatorEvent && "clientY" in activatorEvent && typeof activatorEvent.clientY === "number"
				? activatorEvent.clientY + delta.y
				: pointerYRef.current;

		pointerYRef.current = pointerY;
		hasPointerPositionRef.current = true;
		return pointerY;
	}, []);

	return {
		sensors,
		onDragStart,
		onDragCancel: finishDrag,
		finishDrag,
		setDragTarget,
		startAutoScroll,
		cancelAutoExpand,
		scheduleAutoExpand,
		trackPointer,
	};
};
